/**
 * Pure predicate for the Terms-update notice banner.
 *
 * A signed-in user sees the notice when we cannot show they accepted the
 * CURRENT version:
 *   - `acceptedTermsVersion` sorts strictly before the current version
 *     (versions are ISO dates, YYYY-MM-DD, so lexicographic comparison is a
 *     correct date comparison), or
 *   - `acceptedTermsVersion` is missing. Accounts that predate version
 *     stamping are exactly the people who accepted an OLDER version, and the
 *     Terms' notice promise (§13) is to them most of all. The earlier
 *     "stay quiet on null" reading meant the notice reached ~2% of accounts
 *     (2 of 105 stamped at the 2026-07-29 drill). Signup stamps the current
 *     version, so new accounts never see a spurious banner, and dismissal is
 *     one click.
 *
 * Client-safe: no imports, no side effects. Dismissal (localStorage) is
 * handled by the component, not here.
 */
export function shouldShowTosNotice(
  acceptedTermsVersion: string | null | undefined,
  currentVersion: string,
  /**
   * When the account itself was created (ISO string or Date). An account that
   * did not exist when the current version was published cannot have accepted
   * an earlier one, so there is nothing to notify it about.
   *
   * This exists because GUESTS are never stamped: `lib/auth/guest.ts` creates
   * the row without an `acceptedTermsVersion`, so the missing-value branch above
   * fired for every guest and a visitor who had existed for four seconds was
   * told "Our Terms of Service were updated on July 31, 2026" — a statement with
   * no meaning for someone who never saw the previous version, shown on top of
   * an app that was still loading.
   *
   * Deliberately NOT solved by stamping guests at creation: an unstamped legacy
   * human SHOULD still see the notice (that is the §13 promise this predicate
   * was widened for), and creation time is what actually distinguishes the two.
   */
  createdAt?: string | Date | null,
): boolean {
  if (!currentVersion) return false;

  if (createdAt) {
    // Versions are ISO dates (YYYY-MM-DD); take the same prefix of the account
    // timestamp so the comparison stays a plain lexicographic date compare.
    const created =
      createdAt instanceof Date ? createdAt.toISOString().slice(0, 10) : String(createdAt).slice(0, 10);
    if (created >= currentVersion) return false;
  }

  if (!acceptedTermsVersion) return true;
  return acceptedTermsVersion < currentVersion;
}
